import React from 'react';
import { Helmet } from 'react-helmet';
import { MapPin, Clock, Users, Mountain } from 'lucide-react';
import Header from '../../components/ui/Header';

const TrailDetailsAndThereWasWaterLifeOfPeopleInMountainsAndWater = () => {
  return (
    <>
      <Helmet>
        <title>I BI VODA – Život ljudi u planini i vodi - Creative Twinning</title>
        <meta
          name="description"
          content="Otkrijte stazu kroz Goliju gde voda ima dušu. Istražite vodenice, potoke i izvore koji su vekovima oblikovali život planinske zajednice i njene tradicije." />
        <meta name="keywords" content="Golija, voda, vodenice, planina, Srbija, kulturno nasleđe, tradicija, Brevina, potoci" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section */}
          <div className="relative h-96 bg-cover bg-center" style={{ backgroundImage: `url('https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763508214/golija_reka_ld4u3l.jpg')` }}>
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <h1 className="text-4xl md:text-6xl font-bold mb-4">
                  I BI VODA – Život ljudi u planini i vodi
                </h1>
                <p className="text-xl md:text-2xl">
                  Put koji se ne pamti nogama, nego srcem
                </p>
              </div>
            </div>
          </div>

          {/* Trail Info */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <MapPin className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Dužina</h3>
                <p className="text-gray-600">oko 8 km</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Clock className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Trajanje</h3>
                <p className="text-gray-600">3–4 sata</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Mountain className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Težina</h3>
                <p className="text-gray-600">Laka do srednja</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Users className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Za koga</h3>
                <p className="text-gray-600">Porodice, biciklisti</p>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">O stazi</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Na Goliji, voda je oduvek imala dušu. U njenim potocima, izvorima i vodenicama krije se priča o životu, 
                o ljudima koji su naučili da slušaju planinu i da poštuju svaku kap. Staza „I bi voda – Život ljudi u planini i vodi" 
                povezuje izvore, reke i bačije, pokazujući kako je voda oblikovala svakodnevicu stanovnika Golije.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Kroz Grmičak i okolna sela, posetioci otkrivaju mirise šume, šum potoka i tišinu koja uči strpljenju. 
                Ovo je put koji se ne pamti nogama, nego srcem.
              </p>
            </div>

            {/* Natural Attractions */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Prirodne atrakcije</h2>
              
              {/* Vodenice */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Vodenice – ritam vode i brašna</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Uz stazu se mogu videti stare vodenice – kamene kućice sa drvenim točkovima, u kojima se nekada mlelo žito, 
                      a danas čuvaju sećanje. U njima se danju mlelo, a noću su, prema predanju, dolazile vile i duhovi.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Kažu da su vodenice bile „na pola Božije, na pola đavolje". Zvuk točka koji se okreće u vodi i danas 
                      odzvanja kao uspomena na život u skladu s prirodom.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228206/Fotografija_3.2_-_Lutica_uszbnt.jpg"
                      alt="Stara kamena vodenica sa drvenim točkom na planini Goliji, čuvar tradicije mlevenja žita"
                      className="w-full h-64 object-cover" />
                  </div>
                </div>
              </div>

              {/* Brevina i planinski potoci */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Brevina i planinski potoci</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763457955/brevina_3.3-_to_kraljevo_fi1dpr.jpg"
                      alt="Brevina reka koja se vijuga kroz dolinu sa čistom planinskom vodom i zelenilom oko obala"
                      className="w-full h-64 object-cover" />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Nizvodno od Grmičaka, u dolini Brevine reke, voda pravi male virove i jezerca. Povremeno se u njoj 
                      ogleda nebo, a ponekad samo krošnje drveća.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      To je mesto gde Golija šapuće – nežno i postojano. Ovde se, kažu, voda rađa, a čovek smiruje.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cultural Heritage */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Kulturno nasleđe</h2>
              
              {/* Vodenice – čuvari vremena */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Vodenice – čuvari vremena</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763457938/3.4_vodenice_-sajt_dobro_jutro_xwd3a7.jpg"
                      alt="Drvene vodenice uz potok, tradicionalno graditeljstvo i centar seoskog života"
                      className="w-full h-64 object-cover" />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Duž staze nalaze se ostaci drvenih vodenica koje su vekovima bile srce svakog sela. Tu su ljudi mlele žito, 
                      razgovarali, dogovarali brakove i učili decu prvim poslovima.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      U njima se oseća miris drva, vlage i svežeg brašna. U narodnim pričama vodenice su bile i mesta susreta vila 
                      i duhova – tačka između stvarnog i mitskog sveta.
                    </p>
                  </div>
                </div>
              </div>

              {/* Crkva Svete Trojice */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Crkva Svete Trojice – svetinja na proplanku</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      U obližnjem zaseoku nalazi se mala crkva Svete Trojice, okružena borovima i kamenim zidom. 
                      Skromna i tiha, u proleće zamiriše na sveže cveće, a u jesen na dim sveća.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Crkva je i danas mesto okupljanja, molitve i zajedništva.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763457961/CRKVA_SV._TROJICE_-RUDNO_BUNAR_3.5izvor_drustvene_mreze_gyrdqm.jpg"
                      alt="Crkva Svete Trojice okružena borovima i kamenim zidom, mesto mira i duhovnosti"
                      className="w-full h-64 object-cover" />
                  </div>
                </div>
              </div>
            </div>

            {/* Itinerary */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Predlog programa posete</h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Osnovne informacije</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Dužina staze:</strong> oko 8 km</li>
                    <li><strong>Trajanje:</strong> 3–4 sata laganog hoda</li>
                    <li><strong>Težina:</strong> laka do srednje zahtevna</li>
                    <li><strong>Preporučeno za:</strong> ljubitelje prirode, porodice, fotografe, planinare i bicikliste</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Predlog obilaska</h3>
                  <ol className="space-y-2 text-gray-700 list-decimal list-inside">
                    <li>Početak u selu Grmičak</li>
                    <li>Obilazak vodenica i izvora Vrela</li>
                    <li>Kratki odmor kod Padališta</li>
                    <li>Nastavak do crkve Svete Trojice</li>
                    <li>Povratak kroz dolinu Brevine reke</li>
                  </ol>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 font-medium">
                  Najlepše vreme za posetu je proleće, kada voda šumi najglasnije, i jesen, kada se planina ogleda u svojim bojama.
                </p>
              </div>
            </div>

            {/* Accommodation */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Gde jesti i prespavati</h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Domaćinstvo Šekler */}
                <div className="p-6 border border-gray-200 rounded-lg">
                  <div className="mb-4 rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763457942/3.6.jpg_mini_qv0agc.jpg"
                      alt="Domaćinstvo Šekler sa drvenim kućicama u prirodnom okruženju planine"
                      className="w-full h-48 object-cover" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Domaćinstvo Šekler</h4>
                  <div className="text-sm text-gray-600 space-y-1 mb-3">
                    <p>📍 Rudno 27, 36222 Kraljevo</p>
                    <p>📞 +381 64 1276977</p>
                    <p>✉️ domacinstvosekler@gmail.com</p>
                    <p>🌐 www.seoski-turizam-golija.com</p>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Šest drvenih kućica sa domaćim sirom, kajmakom i džemom od šumskog voća. 
                    Organizuju vožnje kvadovima, jahanje i radionice o bilju.
                  </p>
                </div>

                {/* Pansion Nebo */}
                <div className="p-6 border border-gray-200 rounded-lg">
                  <div className="mb-4 rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763457945/3.7.jpg_mini_c6xvbu.jpg"
                      alt="Pansion Nebo na visokoj planini sa pogledom na horizont i prirodu"
                      className="w-full h-48 object-cover" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Pansion Nebo</h4>
                  <div className="text-sm text-gray-600 space-y-1 mb-3">
                    <p>📍 Srnjača, Rudno, 36222 Kraljevo</p>
                    <p>📞 +381 64 1173262</p>
                    <p>✉️ srnjaca@gmail.com</p>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Na visini od 1.250 metara. Domaćini nude jela od heljde, pastrmke i biljne čajeve 
                    pripremljene od trava s Golije.
                  </p>
                </div>

                {/* Vila Selena */}
                <div className="p-6 border border-gray-200 rounded-lg">
                  <div className="mb-4 rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763457949/3.8.jpg_mini_c66svt.jpg"
                      alt="Vila Selena sa pogledom na pašnjake, dom umetnosti i prirode"
                      className="w-full h-48 object-cover" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Vila Selena</h4>
                  <div className="text-sm text-gray-600 space-y-1 mb-3">
                    <p>📍 Rudno 48, 36222 Kraljevo</p>
                    <p>📞 +381 64 1900851</p>
                    <p>✉️ vilaselena@yahoo.com</p>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Dom umetnosti i prirode sa pogledom na beskrajne pašnjake. 
                    Održava se umetnička kolonija „Golija–Rudno".
                  </p>
                </div>

                {/* Domaćinstvo Milanko Dragojlović */}
                <div className="p-6 border border-gray-200 rounded-lg">
                  <div className="mb-4 rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763457952/3.9_maxjcs.jpg"
                      alt="Domaćinstvo Dragojlović u podnožju Bzovika sa organskim proizvodima"
                      className="w-full h-48 object-cover" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Domaćinstvo Milanko Dragojlović</h4>
                  <div className="text-sm text-gray-600 space-y-1 mb-3">
                    <p>📍 Bzovik 27, 36222 Kraljevo</p>
                    <p>📞 +381 64 1701687</p>
                    <p>✉️ bzovickiraj@gmail.com</p>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Poznato po organskoj hrani, domaćem siru i medu. Kućna atmosfera, 
                    šetnje po okolnim brdima i priče o starim vremenima.
                  </p>
                </div>
              </div>

              {/* Must Not Miss */}
              <div className="mt-12 bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Ne propustite</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-gray-700">
                    <li>• Šetnju kroz Grmičak i Vrela</li>
                    <li>• Vodopade Izubre</li>
                    <li>• Košaninove bare</li>
                  </ul>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Crkvu Svete Trojice</li>
                    <li>• Degustaciju domaće hrane u seoskim domaćinstvima</li>
                    <li>• Susret s meštanima koji čuvaju priče o vodi i životu</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default TrailDetailsAndThereWasWaterLifeOfPeopleInMountainsAndWater;